import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import {
  INFERENCE_STATUSES,
  InferenceStatus,
  LayerAlias,
  LAYERS,
  MULTISPECTRAL_FILTERS_OPTIONS,
  UnitTypes,
  UserPolices
} from "constant";
import features from "features";
import usePolicy from "hooks/usePolicy";
import { MapLayerInterface as MapLayerDataInterface } from "scenes/LayersManager/interfaces";

import CommonButton from "components/buttons/CommonButton/CommonButton";
import OptionButton from "components/buttons/OptionButton/OptionButton";
import ShowHideButton from "components/buttons/ShowHideButton/ShowHideButton";
import InferenceAction from "components/InferenceAction/InferenceAction";
import InferenceStatusComponent from "components/InferenceStatusComponent/InferenceStatusComponent";
import Range from "components/Range/Range";
import Switch from "components/Switch/Switch";

import ChartIcon from "assets/icons/bar-chart.svg";
import CameraIcon from "assets/icons/camera.svg";

import styles from "./map-layer.module.scss";

interface MapLayerInterface extends MapLayerDataInterface {
  isPolygon?: boolean;
  setView?: (...args: any) => void;
  handleRescaleProvidersPortalVisibility?: (...args: any) => void;
}

const MapLayer = ({
  name,
  isActive,
  alias,
  filter,
  opacity,
  isPolygon,
  providers,
  isInferenceStatusWaiting,
  inference,
  setView,
  handleRescaleProvidersPortalVisibility
}: MapLayerInterface) => {
  const dispatch = useDispatch();

  const { id } = useParams();
  const { hasPolicy } = usePolicy();

  const isOnlyStart = useMemo(
    () => alias === LayerAlias.SENSOR_FUSION,
    [alias]
  );

  const isInit = useMemo(
    () => providers?.length > 0 || isPolygon,
    [isPolygon, providers?.length]
  );

  const onSetLayerIsActive = useCallback(
    (isActive: boolean) => () => {
      isPolygon
        ? dispatch(
            features.map.actions.setAriaIsActive({
              projectId: id,
              alias,
              isActive
            })
          )
        : dispatch(
            features.layersManager.actions.setLayerIsActive({
              params: { alias },
              fields: { isActive, isGeneralActive: true }
            })
          );
    },
    [alias, dispatch, isPolygon, id]
  );

  const onLayerFilterChange = useCallback(
    (filter: any, alias: string) => () => {
      dispatch(
        features.layersManager.actions.setLayerFilter({ filter, alias })
      );
    },
    [dispatch]
  );

  const onChangeOpacity = useCallback(
    (alias: LayerAlias) => (opacity) => {
      dispatch(
        features.layersManager.actions.setLayerOpacity({ opacity, alias })
      );
    },
    [dispatch]
  );

  const onRescalePercents = useCallback(
    (alias: LayerAlias) => (percents) => {
      dispatch(
        features.layersManager.actions.setLayerFilter({
          filter: { ...filter, rescalePercents: percents },
          alias
        })
      );
    },
    [dispatch, filter]
  );

  const onManageProviders = useCallback(() => {
    const isUseFileManager = LAYERS[alias].isUseFileManager;
    const isUseFoldersFilesManager = LayerAlias.RGB2 === alias;

    dispatch(
      features.modal.actions.showModal({
        modalType: isUseFoldersFilesManager
          ? "PROVIDER_FOLDERS_FILES_MANAGER_MODAL"
          : isUseFileManager
          ? "PROVIDER_FILE_MANAGER_MODAL"
          : "PROVIDER_MANAGER_MODAL",
        modalProps: {
          title: `${LAYERS[alias].name} ${
            isUseFileManager ? "File" : "Orthophoto"
          } Manager`,
          alias,
          id,
          isUseFileManager,
          initProviders: providers,
          onHide: (updatedProviders) => {
            const firstProviderCenter = updatedProviders?.[0]?.data?.center;

            if (firstProviderCenter) {
              setView(
                {
                  lat: firstProviderCenter[1],
                  lng: firstProviderCenter[0]
                },
                firstProviderCenter[2]
              );
            }

            // dispatch(
            //   features.layersManager.actions.updateLayerProviders({
            //     alias,
            //     providers: updatedProviders
            //   })
            // );
          }
        }
      })
    );
  }, [alias, dispatch, id, providers, setView]);

  // const startSensorFusingInference = useCallback(() => {
  //   dispatch(
  //     features.layersManager.actions.startSensorFusingInferenceRequest({
  //       params: { id, alias }
  //     })
  //   );
  // }, [alias, dispatch, id]);

  // const cancelSensorFusingInference = useCallback(() => {
  //   dispatch(
  //     features.map.actions.cancelInferenceRequest({
  //       params: { id, alias }
  //     })
  //   );
  // }, [alias, dispatch, id]);

  //TODO you know what to do
  const [isShowPhotos, setIsShowPhotos] = useState(false);
  const showRGB2Photos = useCallback(
    (e) => {
      const isActive = e.target.checked;
      if (isActive)
        dispatch(
          features.unitsManager.actions.fetchUnitsRequest({
            params: {
              projectId: id,
              type: UnitTypes.PHOTO,
              alias
            },
            onSuccess: () => {
              setIsShowPhotos(isActive);
            }
          })
        );
      else {
        dispatch(
          features.unitsManager.actions.clearUnits({
            params: {
              projectId: id,
              type: UnitTypes.PHOTO,
              alias
            }
          })
        );
        setIsShowPhotos(isActive);
      }
    },
    [alias, dispatch, id]
  );
  const onEditRescaleProviders = useCallback(() => {
    handleRescaleProvidersPortalVisibility(alias);
  }, [alias, handleRescaleProvidersPortalVisibility]);

  return (
    <div className={styles["layer-wrapper"]} id={`${alias}-layer-block`}>
      <ShowHideButton
        title={name}
        onClick={onSetLayerIsActive(!isActive)}
        isActive={isActive}
        isDisabled={!isInit && !isOnlyStart}
      />
      {!isPolygon && !isOnlyStart && (
        <Range
          value={opacity ?? 100}
          containerClassName={styles["layer__range-container"]}
          isDisabled={!isInit || !isActive}
          onChange={onChangeOpacity(alias)}
        />
      )}
      {alias === LayerAlias.MULTISPECTRAL && (
        <>
          <div className={styles["multispectral__options"]}>
            {MULTISPECTRAL_FILTERS_OPTIONS.map((item) => {
              return (
                <OptionButton
                  onClick={onLayerFilterChange(item, alias)}
                  key={`layer-param-${item.name}`}
                  isDisabled={!isActive}
                  isActive={filter?.name === item.name && isActive}
                >
                  {item.name}
                </OptionButton>
              );
            })}
          </div>
          {filter?.rescalePercents && (
            <Range
              value={filter?.rescalePercents}
              containerClassName={styles["layer__range-container"]}
              isDisabled={!isInit || !isActive}
              onChange={onRescalePercents(alias)}
            />
          )}
        </>
      )}

      {!isPolygon && hasPolicy(UserPolices.INFERENCE_GENERAL) && (
        <div className={styles["layer__buttons-container"]}>
          {LayerAlias.SENSOR_FUSION === alias ? (
            // <CommonButton
            //   type="button"
            //   onClick={
            //     true ? startSensorFusingInference : cancelSensorFusingInference
            //   }
            //   icon={PlayIcon}
            //   isLoading={isInferenceStatusWaiting}
            //   disabled={isInferenceStatusWaiting}
            // >
            //   Start
            //   {/* {isInferenceProgress ? "Cancel inferencing" : "Start"} */}
            // </CommonButton>
            <div className={styles["layer__inference"]}>
              <InferenceAction
                id={id}
                alias={LayerAlias.SENSOR_FUSION}
                status={
                  INFERENCE_STATUSES[
                    inference?.status ?? InferenceStatus.PREPARED
                  ]
                }
                isLoading={isInferenceStatusWaiting}
                disabled={isInferenceStatusWaiting}
              />

              <InferenceStatusComponent
                status={INFERENCE_STATUSES[inference?.status]}
                completionPercentage={
                  inference?.status === InferenceStatus.PROCESSING
                    ? inference?.data?.completion_percentage
                    : undefined
                }
              />
            </div>
          ) : (
            <div className={styles["layer__controls"]}>
              <CommonButton
                type="button"
                onClick={onManageProviders}
                icon={CameraIcon}
              >
                Manage
              </CommonButton>
              {alias === LayerAlias.RGB2 && (
                <Switch
                  label="show photo"
                  value={isShowPhotos}
                  onChange={showRGB2Photos}
                  isDisabled={!isInit}
                />
              )}

              {alias === LayerAlias.MULTISPECTRAL && providers?.length > 0 && (
                <CommonButton
                  onClick={onEditRescaleProviders}
                  icon={ChartIcon}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default MapLayer;
